import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import YMRSForm from "./YMRSForm";
import { useDispatch } from "react-redux";
import { createYmrsAssessment } from "../../../store/slices/ymrsAssessmentsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice"; // Added this import

const AddYMRS = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        patientName: "",
        date: "",
        assessedBy: "",
        elevatedMood: "",
        motorActivity: "",
        sexualInterest: "",
        sleep: "",
        irritability: "",
        speech: "",
        thoughtDisorder: "",
        thoughtContent: "",
        disruptiveBehavior: "",
        appearance: "",
        insight: ""
    });

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            patientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to prefill patient name for YMRS:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const calculateTotal = () => {
        const fields = [
            "elevatedMood", "motorActivity", "sexualInterest", "sleep",
            "irritability", "speech", "thoughtDisorder", "thoughtContent",
            "disruptiveBehavior", "appearance", "insight"
        ];
        return fields.reduce((sum, field) => sum + Number(formData[field] || 0), 0);
    };


    const isComplete = () => {
        const fields = [
            "elevatedMood", "motorActivity", "sexualInterest", "sleep",
            "irritability", "speech", "thoughtDisorder", "thoughtContent",
            "disruptiveBehavior", "appearance", "insight"
        ];
        return fields.every(field => formData[field] !== "" && formData[field] !== undefined);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isComplete()) {
            Swal.fire("Incomplete", "Please complete all 11 items.", "warning");
            return;
        }

        // Map numeric values to their corresponding labels
        const questions = {
            elevatedMood: [
                "Absent",
                "Mildly or possibly increased on questioning",
                "Definite subjective elevation; optimistic; self-confident; cheerful; appropriate to content",
                "Elevated, inappropriate to content; humorous",
                "Euphoric; inappropriate laughter, singing"
            ],
            motorActivity: [
                "Absent",
                "Subjectively increased",
                "Animated; gestures increased",
                "Excessive energy; hyperactive at times; restless (can be calmed)",
                "Motor excitement; continuous hyperactivity (cannot be calmed)"
            ],
            sexualInterest: [
                "Normal; not increased",
                "Mildly or possibly increased",
                "Definite subjective increase on questioning",
                "Spontaneous sexual content; elaborates on sexual matters; hypersexual by self-report",
                "Overt sexual acts (toward patients, staff, or interviewer)"
            ],
            sleep: [
                "Reports no decrease in sleep",
                "Sleeping less than normal amount by up to one hour",
                "Sleeping less than normal by more than one hour",
                "Reports decreased need for sleep",
                "Denies need for sleep"
            ],
            irritability: [
                "Absent",
                "Subjectively increased",
                "Irritable at times during interview; recent episodes of anger or annoyance on ward",
                "Frequently irritable during interview; short or curt throughout",
                "Hostile, uncooperative; interview impossible"
            ],
            speech: [
                "No increase",
                "Feels talkative",
                "Increased rate or amount at times, verbose at times",
                "Push; consistently increased rate and amount; difficult to interrupt",
                "Pressured; uninterruptible, continuous speech"
            ],
            thoughtDisorder: [
                "Absent",
                "Circumstantial; mild distractibility; quick thoughts",
                "Distractible; loses goal of thought; changes topics frequently; racing thoughts",
                "Flight of ideas; tangentiality; difficult to follow; rhyming; echolalia",
                "Incoherent; communication impossible"
            ],
            thoughtContent: [
                "Normal",
                "Questionable plans; new interests",
                "Special project(s); hyper-religious",
                "Grandiose or paranoid ideas; ideas of reference",
                "Delusions; hallucinations"
            ],
            disruptiveBehavior: [
                "Absent, cooperative",
                "Sarcastic; loud at times, guarded",
                "Demanding; threats on ward",
                "Threatens interviewer; shouting; interview difficult",
                "Assaultive; destructive; interview impossible"
            ],
            appearance: [
                "Appropriate dress and grooming",
                "Minimally unkempt",
                "Poorly groomed; moderately disheveled; overdressed",
                "Disheveled; partly clothed; garish makeup",
                "Completely unkempt; decorated; bizarre garb"
            ],
            insight: [
                "Present; admits illness; agrees with need for treatment",
                "Possibly ill",
                "Admits behavior change, but denies illness",
                "Admits possible change in behavior; but denies illness",
                "Denies any behavior change"
            ]
        };

        const payload = {
            case_id: formData.caseId,
            patient_id: formData.patientId,
            client_name: formData.patientName,
            date: formData.date,
            assessed_by: formData.assessedBy,
            elevated_mood_value: questions.elevatedMood[formData.elevatedMood] || "",
            elevated_mood_score: parseInt(formData.elevatedMood || 0, 10),
            motor_activity_value: questions.motorActivity[formData.motorActivity] || "",
            motor_activity_score: parseInt(formData.motorActivity || 0, 10),
            sexual_interest_value: questions.sexualInterest[formData.sexualInterest] || "",
            sexual_interest_score: parseInt(formData.sexualInterest || 0, 10),
            sleep_value: questions.sleep[formData.sleep] || "",
            sleep_score: parseInt(formData.sleep || 0, 10),
            irritability_value: questions.irritability[formData.irritability] || "",
            irritability_score: parseInt(formData.irritability || 0, 10),
            speech_value: questions.speech[formData.speech] || "",
            speech_score: parseInt(formData.speech || 0, 10),
            thought_disorder_value: questions.thoughtDisorder[formData.thoughtDisorder] || "",
            thought_disorder_score: parseInt(formData.thoughtDisorder || 0, 10),
            thought_content_value: questions.thoughtContent[formData.thoughtContent] || "",
            thought_content_score: parseInt(formData.thoughtContent || 0, 10),
            disruptive_behavior_value: questions.disruptiveBehavior[formData.disruptiveBehavior] || "",
            disruptive_behavior_score: parseInt(formData.disruptiveBehavior || 0, 10),
            appearance_value: questions.appearance[formData.appearance] || "",
            appearance_score: parseInt(formData.appearance || 0, 10),
            insight_value: questions.insight[formData.insight] || "",
            insight_score: parseInt(formData.insight || 0, 10),
            total_score: calculateTotal()
        };

        try {
            await dispatch(createYmrsAssessment(payload)).unwrap();
            Swal.fire({
                title: "Saved!",
                text: `YMRS Assessment completed. Total Score: ${payload.total_score}`,
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (error) {
            Swal.fire("Error", error || "Failed to save YMRS assessment", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <YMRSForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                    >
                                        <i className="ri-save-2-line me-1"></i>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddYMRS;
